/**
 * In-App Notification Service
 * Generates notifications based on expense and nomination workflow actions.
 * Uses GraphQL (DynamoDB) in production, localStorage in demo mode.
 *
 * Notification use cases covered:
 *
 * EXPENSE WORKFLOW:
 * 1. Employee submits expense → Manager gets notified
 * 2. Manager approves expense → Employee notified + Finance Admin notified (next approver)
 * 3. Manager rejects expense → Employee notified
 * 4. Finance approves expense → Employee notified
 * 5. Finance rejects expense → Employee notified
 * 6. Expense reimbursed → Employee notified
 *
 * NOMINATION WORKFLOW:
 * 7. Manager creates/submits nomination → Reviewer & Admin notified
 * 8. Reviewer moves nomination to Under Review → Manager (nominator) notified
 * 9. Reviewer approves nomination → Manager notified + Admin notified
 * 10. Reviewer rejects nomination → Manager notified
 * 11. Admin publishes nomination → All employees notified (broadcast)
 * 12. Someone comments on a published nomination → Nominator notified
 */

import {
  AppNotification,
  NotificationCategory,
  NotificationPriority,
} from '../types/notification';
import { GraphQLNotificationService } from './graphqlNotification';
import { isDemoMode } from './mockData';
import { objectiveLinkForApprover, objectiveLinkForEmployee } from './pmsObjectiveState';

const STORAGE_KEY_PREFIX = 'creyenteone_notifications_';

const generateId = (): string =>
  `notif_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const now = (): string => new Date().toISOString();

// ─── Storage helpers (localStorage fallback for demo mode) ───

const getStorageKey = (userId: string) => `${STORAGE_KEY_PREFIX}${userId}`;

const getNotificationsFromLocalStorage = (userId: string): AppNotification[] => {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveNotificationsToLocalStorage = (userId: string, notifications: AppNotification[]) => {
  const trimmed = notifications.slice(0, 100);
  localStorage.setItem(getStorageKey(userId), JSON.stringify(trimmed));
};

const pushNotificationToLocalStorage = (notification: AppNotification) => {
  const existing = getNotificationsFromLocalStorage(notification.recipientId);
  saveNotificationsToLocalStorage(notification.recipientId, [notification, ...existing]);
};

// ─── Public API (uses GraphQL in production, localStorage in demo) ───

export const getNotifications = async (
  userId: string,
  options: { force?: boolean } = {}
): Promise<AppNotification[]> => {
  if (isDemoMode()) {
    return getNotificationsFromLocalStorage(userId);
  }
  return GraphQLNotificationService.getNotifications(userId, options);
};

export const getUnreadCount = async (userId: string): Promise<number> => {
  if (isDemoMode()) {
    return getNotificationsFromLocalStorage(userId).filter((n) => !n.read).length;
  }
  return GraphQLNotificationService.getUnreadCount(userId);
};

export const markAsRead = async (userId: string, notificationId: string) => {
  if (isDemoMode()) {
    const all = getNotificationsFromLocalStorage(userId);
    const updated = all.map((n) =>
      n.id === notificationId ? { ...n, read: true } : n
    );
    saveNotificationsToLocalStorage(userId, updated);
  } else {
    await GraphQLNotificationService.markAsRead(userId, notificationId);
  }
};

export const markAllAsRead = async (userId: string) => {
  if (isDemoMode()) {
    const all = getNotificationsFromLocalStorage(userId);
    const updated = all.map((n) => ({ ...n, read: true }));
    saveNotificationsToLocalStorage(userId, updated);
  } else {
    // Try the bulk mutation first
    await GraphQLNotificationService.markAllAsRead(userId);

    // Verify it worked — if bulk mutation isn't implemented in backend,
    // fall back to marking each notification individually
    const remaining = await GraphQLNotificationService.getNotifications(userId, { force: true });
    const stillUnread = remaining.filter((n) => !n.read);
    if (stillUnread.length > 0) {
      console.log(`[markAllAsRead] Bulk mutation left ${stillUnread.length} unread, marking individually...`);
      await Promise.all(
        stillUnread.map((n) => GraphQLNotificationService.markAsRead(userId, n.id))
      );
      console.log('[markAllAsRead] All notifications marked individually');
    }
  }
};

export const deleteNotification = async (userId: string, notificationId: string) => {
  if (isDemoMode()) {
    const all = getNotificationsFromLocalStorage(userId);
    saveNotificationsToLocalStorage(userId, all.filter((n) => n.id !== notificationId));
  } else {
    await GraphQLNotificationService.deleteNotification(userId, notificationId);
  }
};

export const clearNotifications = async (userId: string) => {
  console.log('[Notification] Clearing all notifications for user:', userId);

  if (isDemoMode()) {
    localStorage.removeItem(getStorageKey(userId));
    console.log('[Notification] Cleared from localStorage (demo mode)');
  } else {
    // In production, delete all notifications for this user via GraphQL
    try {
      const result = await GraphQLNotificationService.clearAllNotifications(userId);
      if (result.success) {
        console.log('[Notification] Successfully cleared all notifications');
      } else {
        console.error('[Notification] Failed to clear notifications:', result.errors);
      }
    } catch (error) {
      console.error('[Notification] Error clearing notifications:', error);
    }
  }
};

const pushNotification = async (notification: Omit<AppNotification, 'id'>) => {
  console.log('[inAppNotification] Pushing notification:', {
    recipientId: notification.recipientId,
    title: notification.title,
    category: notification.category
  });

  if (isDemoMode()) {
    pushNotificationToLocalStorage({ ...notification, id: generateId() });
    console.log('[inAppNotification] Notification saved to localStorage (demo mode)');
  } else {
    try {
      await GraphQLNotificationService.createNotification({
        userId: notification.recipientId,
        category: notification.category,
        priority: notification.priority,
        title: notification.title,
        message: notification.message,
        icon: notification.icon,
        link: notification.link,
        actorName: notification.actorName,
      });
      console.log('[inAppNotification] Notification created in database');
    } catch (error) {
      console.error('[inAppNotification] Failed to create notification:', error);
    }
  }
};

// ─── EXPENSE NOTIFICATIONS ───

/** 1. Employee submits expense → notify manager */
export const notifyExpenseSubmitted = async (
  managerId: string,
  employeeName: string,
  expenseTitle: string,
  amount: number,
  expenseId: string
) => {
  await pushNotification({
    category: NotificationCategory.EXPENSE,
    priority: NotificationPriority.MEDIUM,
    title: 'New Expense Submitted',
    message: `${employeeName} submitted "${expenseTitle}" for ₹${amount.toLocaleString()}`,
    icon: '📋',
    link: `/expenses/${expenseId}`,
    read: false,
    createdAt: now(),
    recipientId: managerId,
    actorName: employeeName,
  });
};

/** 2. Manager approves expense → notify employee + finance admin */
export const notifyExpenseManagerApproved = async (
  employeeId: string,
  financeAdminIds: string[],
  managerName: string,
  expenseTitle: string,
  amount: number,
  expenseId: string
) => {
  // Notify employee
  await pushNotification({
    category: NotificationCategory.EXPENSE,
    priority: NotificationPriority.MEDIUM,
    title: 'Expense Approved by Manager',
    message: `${managerName} approved your expense "${expenseTitle}" (₹${amount.toLocaleString()}). Forwarded to Finance.`,
    icon: '✅',
    link: `/expenses/${expenseId}`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
  // Notify finance admins
  for (const fId of financeAdminIds) {
    await pushNotification({
      category: NotificationCategory.EXPENSE,
      priority: NotificationPriority.MEDIUM,
      title: 'Expense Pending Finance Approval',
      message: `"${expenseTitle}" (₹${amount.toLocaleString()}) approved by ${managerName}, awaiting your review.`,
      icon: '💰',
      link: `/expenses/${expenseId}`,
      read: false,
      createdAt: now(),
      recipientId: fId,
      actorName: managerName,
    });
  }
};

/** 3. Manager rejects expense → notify employee */
export const notifyExpenseManagerRejected = async (
  employeeId: string,
  managerName: string,
  expenseTitle: string,
  expenseId: string,
  reason?: string
) => {
  await pushNotification({
    category: NotificationCategory.EXPENSE,
    priority: NotificationPriority.HIGH,
    title: 'Expense Rejected',
    message: `${managerName} rejected your expense "${expenseTitle}"${reason ? `: ${reason}` : ''}`,
    icon: '❌',
    link: `/expenses/${expenseId}`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

/** 4. Finance approves expense → notify employee */
export const notifyExpenseFinanceApproved = async (
  employeeId: string,
  financeAdminName: string,
  expenseTitle: string,
  amount: number,
  expenseId: string
) => {
  await pushNotification({
    category: NotificationCategory.EXPENSE,
    priority: NotificationPriority.MEDIUM,
    title: 'Expense Finance Approved',
    message: `${financeAdminName} gave finance approval for "${expenseTitle}" (₹${amount.toLocaleString()})`,
    icon: '✅',
    link: `/expenses/${expenseId}`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: financeAdminName,
  });
};

/** 5. Finance rejects expense → notify employee */
export const notifyExpenseFinanceRejected = async (
  employeeId: string,
  financeAdminName: string,
  expenseTitle: string,
  expenseId: string,
  reason?: string
) => {
  await pushNotification({
    category: NotificationCategory.EXPENSE,
    priority: NotificationPriority.HIGH,
    title: 'Expense Rejected by Finance',
    message: `${financeAdminName} rejected "${expenseTitle}"${reason ? `: ${reason}` : ''}`,
    icon: '❌',
    link: `/expenses/${expenseId}`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: financeAdminName,
  });
};

/** 6. Expense reimbursed → notify employee */
export const notifyExpenseReimbursed = async (
  employeeId: string,
  expenseTitle: string,
  amount: number,
  expenseId: string
) => {
  await pushNotification({
    category: NotificationCategory.EXPENSE,
    priority: NotificationPriority.LOW,
    title: 'Expense Reimbursed',
    message: `Your expense "${expenseTitle}" (₹${amount.toLocaleString()}) has been reimbursed.`,
    icon: '💸',
    link: `/expenses/${expenseId}`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
  });
};

/** 6b. Employee deletes expense → notify manager */
export const notifyExpenseDeleted = async (
  managerId: string,
  employeeName: string,
  expenseTitle: string,
  amount: number
) => {
  await pushNotification({
    category: NotificationCategory.EXPENSE,
    priority: NotificationPriority.MEDIUM,
    title: 'Expense Deleted',
    message: `${employeeName} deleted expense "${expenseTitle}" (₹${amount.toLocaleString()})`,
    icon: '🗑️',
    link: `/manager`,
    read: false,
    createdAt: now(),
    recipientId: managerId,
    actorName: employeeName,
  });
};

// ─── NOMINATION NOTIFICATIONS ───

/** 7. Manager submits nomination → notify reviewer(s) and admin(s) */
// ─── CERTIFICATION REIMBURSEMENT ───

/** Employee raises a reimbursement claim → notify whoever it is now waiting on. */
export const notifyCertificationReimbursementSubmitted = async (
  recipientIds: string[],
  employeeName: string,
  certificationName: string,
  amount: number,
  reviewPath: string
) => {
  for (const recipientId of recipientIds) {
    await pushNotification({
      category: NotificationCategory.CERTIFICATION,
      priority: NotificationPriority.MEDIUM,
      title: 'Certification Reimbursement Submitted',
      message: `${employeeName} submitted a claim for "${certificationName}" (₹${(Number(amount) || 0).toLocaleString()})`,
      icon: '🎓',
      link: reviewPath,
      read: false,
      createdAt: now(),
      recipientId,
      actorName: employeeName,
    });
  }
};

/**
 * Any later movement on a claim — approved onto the next stage, rejected, or reimbursed.
 *
 * One parameterised helper rather than four near-identical ones. A bell entry is a title, a
 * line of text and a link; the four events differ only in those, and four copies would drift.
 * The emails stay separate because their bodies genuinely differ.
 */
export const notifyCertificationReimbursementDecided = async (
  recipientIds: string[],
  entry: { title: string; message: string; link: string; icon: string; priority?: NotificationPriority }
) => {
  for (const recipientId of recipientIds) {
    await pushNotification({
      category: NotificationCategory.CERTIFICATION,
      priority: entry.priority ?? NotificationPriority.MEDIUM,
      title: entry.title,
      message: entry.message,
      icon: entry.icon,
      link: entry.link,
      read: false,
      createdAt: now(),
      recipientId,
    });
  }
};

export const notifyNominationSubmitted = async (
  reviewerIds: string[],
  adminIds: string[],
  managerName: string,
  nomineeName: string,
  awardType: string,
  nominationId: string
) => {
  const allRecipients = [...new Set([...reviewerIds, ...adminIds])];
  for (const rid of allRecipients) {
    await pushNotification({
      category: NotificationCategory.NOMINATION,
      priority: NotificationPriority.MEDIUM,
      title: 'New Nomination Submitted',
      message: `${managerName} nominated ${nomineeName} for ${awardType}`,
      icon: '⭐',
      link: `/nominations/review`,
      read: false,
      createdAt: now(),
      recipientId: rid,
      actorName: managerName,
    });
  }
};

/** 8. Reviewer moves to Under Review → notify nominator */
export const notifyNominationUnderReview = async (
  nominatorId: string,
  reviewerName: string,
  nomineeName: string,
  nominationId: string
) => {
  await pushNotification({
    category: NotificationCategory.NOMINATION,
    priority: NotificationPriority.LOW,
    title: 'Nomination Under Review',
    message: `${reviewerName} is reviewing your nomination for ${nomineeName}`,
    icon: '🔍',
    link: `/nominations/${nominationId}`,
    read: false,
    createdAt: now(),
    recipientId: nominatorId,
    actorName: reviewerName,
  });
};

/** 9. Reviewer approves nomination → notify nominator + admin */
export const notifyNominationApproved = async (
  nominatorId: string,
  adminIds: string[],
  reviewerName: string,
  nomineeName: string,
  awardType: string,
  nominationId: string
) => {
  await pushNotification({
    category: NotificationCategory.NOMINATION,
    priority: NotificationPriority.MEDIUM,
    title: 'Nomination Approved',
    message: `${reviewerName} approved your nomination for ${nomineeName} (${awardType})`,
    icon: '🏆',
    link: `/nominations/${nominationId}`,
    read: false,
    createdAt: now(),
    recipientId: nominatorId,
    actorName: reviewerName,
  });
  for (const aid of adminIds) {
    await pushNotification({
      category: NotificationCategory.NOMINATION,
      priority: NotificationPriority.MEDIUM,
      title: 'Nomination Ready to Publish',
      message: `${nomineeName}'s ${awardType} nomination approved by ${reviewerName}. Ready to publish.`,
      icon: '📢',
      link: `/nominations/admin`,
      read: false,
      createdAt: now(),
      recipientId: aid,
      actorName: reviewerName,
    });
  }
};

/** 10. Reviewer rejects nomination → notify nominator */
export const notifyNominationRejected = async (
  nominatorId: string,
  reviewerName: string,
  nomineeName: string,
  nominationId: string,
  reason?: string
) => {
  await pushNotification({
    category: NotificationCategory.NOMINATION,
    priority: NotificationPriority.HIGH,
    title: 'Nomination Rejected',
    message: `${reviewerName} rejected your nomination for ${nomineeName}${reason ? `: ${reason}` : ''}`,
    icon: '❌',
    link: `/nominations/${nominationId}`,
    read: false,
    createdAt: now(),
    recipientId: nominatorId,
    actorName: reviewerName,
  });
};

/** 11. Admin publishes nomination → broadcast to all employees */
export const notifyNominationPublished = async (
  allUserIds: string[],
  nomineeName: string,
  awardType: string,
  publisherName: string,
  nomineeId?: string
) => {
  for (const uid of allUserIds) {
    // Send a special notification to the nominee
    if (nomineeId && uid === nomineeId) {
      await pushNotification({
        category: NotificationCategory.NOMINATION,
        priority: NotificationPriority.HIGH,
        title: '🏆 You Have Been Recognized!',
        message: `Congratulations! You have been recognized with the ${awardType} award. Check the recognition feed for details!`,
        icon: '🏆',
        link: `/nominations/feed`,
        read: false,
        createdAt: now(),
        recipientId: uid,
        actorName: publisherName,
      });
    } else {
      await pushNotification({
        category: NotificationCategory.NOMINATION,
        priority: NotificationPriority.LOW,
        title: '🎉 New Recognition Published',
        message: `${nomineeName} has been recognized with ${awardType}!`,
        icon: '🎉',
        link: `/nominations/feed`,
        read: false,
        createdAt: now(),
        recipientId: uid,
        actorName: publisherName,
      });
    }
  }
};

/** 12. Someone comments on a published nomination → notify nominator */
export const notifyNominationComment = async (
  nominatorId: string,
  commenterName: string,
  nomineeName: string,
  nominationId: string
) => {
  await pushNotification({
    category: NotificationCategory.NOMINATION,
    priority: NotificationPriority.LOW,
    title: 'New Comment on Nomination',
    message: `${commenterName} commented on ${nomineeName}'s recognition`,
    icon: '💬',
    link: `/nominations/feed`,
    read: false,
    createdAt: now(),
    recipientId: nominatorId,
    actorName: commenterName,
  });
};

// ─── PERFORMANCE NOTIFICATIONS ───

/**
 * PMS 1. Employee submits objective → notify manager.
 *
 * Linked to the APPROVALS queue rather than to the objective list. All four PMS notifications
 * pointed at `/performance/objectives/{id}` whoever they were for, so a manager reviewing
 * somebody else's goal watched "My Objectives" light up in the sidebar — the sidebar marks an
 * entry active when the path starts with it, and that path says the reader is in their own list.
 * objectiveLinkForApprover puts the destination in the address instead, so a click, a refresh and
 * a pasted URL all agree. See pmsObjectiveState.
 */
export const notifyObjectiveSubmitted = async (
  managerId: string,
  employeeName: string,
  objectiveTitle: string,
  objectiveId: string,
  /** A second look, after a return — worth saying, because it tells the manager what to compare. */
  resubmitted = false
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.MEDIUM,
    title: resubmitted ? 'Objective Resubmitted' : 'New Objective Submitted',
    message: resubmitted
      ? `${employeeName} resubmitted objective "${objectiveTitle}" for your approval`
      : `${employeeName} submitted objective "${objectiveTitle}" for your approval`,
    icon: '🎯',
    link: objectiveLinkForApprover(objectiveId),
    read: false,
    createdAt: now(),
    recipientId: managerId,
    actorName: employeeName,
  });
};

/** PMS 2. Manager approves objective → notify employee */
export const notifyObjectiveApproved = async (
  employeeId: string,
  managerName: string,
  objectiveTitle: string,
  objectiveId: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.MEDIUM,
    title: 'Objective Approved',
    message: `${managerName} approved your objective "${objectiveTitle}"`,
    icon: '🎯',
    link: objectiveLinkForEmployee(objectiveId),
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

/** PMS 3. Manager returns/rejects objective → notify employee */
export const notifyObjectiveReturned = async (
  employeeId: string,
  managerName: string,
  objectiveTitle: string,
  objectiveId: string,
  comments?: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.HIGH,
    title: 'Objective Returned',
    message: `${managerName} returned your objective "${objectiveTitle}"${comments ? `: ${comments}` : ''}`,
    icon: '🎯',
    link: objectiveLinkForEmployee(objectiveId),
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

export const notifyObjectiveChangeRequestApproved = async (
  employeeId: string,
  managerName: string,
  objectiveTitle: string,
  objectiveId: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.MEDIUM,
    title: 'Objective Change Request Approved',
    message: `${managerName} approved your change request for "${objectiveTitle}"`,
    icon: '🎯',
    link: objectiveLinkForEmployee(objectiveId),
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

export const notifyObjectiveChangeRequestRejected = async (
  employeeId: string,
  managerName: string,
  objectiveTitle: string,
  objectiveId: string,
  reason?: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.HIGH,
    title: 'Objective Change Request Rejected',
    message: `${managerName} rejected your change request for "${objectiveTitle}"${reason ? `: ${reason}` : ''}`,
    icon: '🎯',
    link: objectiveLinkForEmployee(objectiveId),
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

/** PMS 4. Mid-year review submitted → notify employee */
export const notifyMidYearReviewSubmitted = async (
  employeeId: string,
  managerName: string,
  reviewCycleName: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.MEDIUM,
    title: 'Mid-Year Review Submitted',
    message: `${managerName} submitted your mid-year review for "${reviewCycleName}"`,
    icon: '⭐',
    link: `/performance/reviews`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

/** PMS 5. Final review submitted → notify employee */
export const notifyFinalReviewSubmitted = async (
  employeeId: string,
  managerName: string,
  reviewCycleName: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.MEDIUM,
    title: 'Final Review Submitted',
    message: `${managerName} submitted your final review for "${reviewCycleName}"`,
    icon: '⭐',
    link: `/performance/reviews`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

export const notifyMidYearReviewReminder = async (
  employeeId: string,
  managerName: string,
  reviewCycleName: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.MEDIUM,
    title: 'Mid-Year Review Reminder',
    message: `${managerName} added a mid-year review reminder for "${reviewCycleName}"`,
    icon: '⏰',
    link: `/performance/reviews`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

export const notifyFinalReviewAvailable = async (
  employeeId: string,
  managerName: string,
  reviewCycleName: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.HIGH,
    title: `${reviewCycleName} Published`,
    message: `${managerName} published your year-end review "${reviewCycleName}"`,
    icon: '⭐',
    link: `/performance/reviews`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

/** PMS — High-level review published */
export const notifyHighLevelReviewPublished = async (
  employeeId: string,
  managerName: string,
  reviewCycleName: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.HIGH,
    title: `${reviewCycleName} Published`,
    message: `${managerName} published your review "${reviewCycleName}"`,
    icon: '⭐',
    link: `/performance/reviews`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

/** PMS — Employee acknowledged review confirmation */
export const notifyReviewAcknowledged = async (
  managerId: string,
  employeeName: string,
  reviewCycleName: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.LOW,
    title: 'Review Acknowledged',
    message: `${employeeName} acknowledged the review for "${reviewCycleName}"`,
    icon: '✅',
    link: `/performance/team-reviews`,
    read: false,
    createdAt: now(),
    recipientId: managerId,
    actorName: employeeName,
  });
};

/** PMS 6. Chat message posted → notify other participant */
export const notifyPMSChatMessage = async (
  recipientId: string,
  senderName: string,
  objectiveTitle: string,
  objectiveId: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.LOW,
    title: 'New Chat Message',
    message: `${senderName} sent a message on objective "${objectiveTitle}"`,
    icon: '💬',
    link: objectiveLinkForEmployee(objectiveId),
    read: false,
    createdAt: now(),
    recipientId,
    actorName: senderName,
  });
};

/** PMS 7. Objective/milestone modified by manager → notify employee */
export const notifyObjectiveModifiedByManager = async (
  employeeId: string,
  managerName: string,
  objectiveTitle: string,
  objectiveId: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.MEDIUM,
    title: 'Objective Modified',
    message: `${managerName} modified your objective "${objectiveTitle}"`,
    icon: '🎯',
    link: objectiveLinkForEmployee(objectiveId),
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

/** PMS 8. Deadline reminder → notify managers */
export const notifyDeadlineReminder = async (
  managerIds: string[],
  deadlineType: 'mid-year' | 'end-of-year',
  deadlineDate: string,
  reviewCycleName: string
) => {
  const label = deadlineType === 'mid-year' ? 'mid-year review' : 'end-of-year review';
  for (const managerId of managerIds) {
    await pushNotification({
      category: NotificationCategory.PERFORMANCE,
      priority: NotificationPriority.HIGH,
      title: 'Review Deadline Reminder',
      message: `The ${label} deadline for "${reviewCycleName}" is ${deadlineDate}. Please complete pending reviews.`,
      icon: '⏰',
      link: `/performance/team-reviews`,
      read: false,
      createdAt: now(),
      recipientId: managerId,
    });
  }
};

/** PMS 9. Review Assigned → notify employee when a review is created for them */
export const notifyReviewAssigned = async (
  employeeId: string,
  managerName: string,
  reviewCycleName: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.MEDIUM,
    title: 'Review Assigned',
    message: `${managerName} created a performance review for you in "${reviewCycleName}"`,
    icon: '📝',
    link: `/performance/reviews`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
};

/** PMS 10. Review Reopened → notify employee + manager when HR reopens a review */
export const notifyReviewReopened = async (
  employeeId: string,
  managerId: string,
  hrAdminName: string,
  reviewCycleName: string,
  reason: string
) => {
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.HIGH,
    title: 'Review Reopened',
    message: `${hrAdminName} reopened your review for "${reviewCycleName}": ${reason}`,
    icon: '🔄',
    link: `/performance/reviews`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: hrAdminName,
  });
  await pushNotification({
    category: NotificationCategory.PERFORMANCE,
    priority: NotificationPriority.HIGH,
    title: 'Review Reopened',
    message: `${hrAdminName} reopened the review for "${reviewCycleName}": ${reason}`,
    icon: '🔄',
    link: `/performance/team-reviews`,
    read: false,
    createdAt: now(),
    recipientId: managerId,
    actorName: hrAdminName,
  });
};

/** PMS 11. Cycle Locked → notify all managers with pending reviews */
export const notifyReviewCycleLocked = async (
  recipientIds: string[],
  hrAdminName: string,
  reviewCycleName: string
) => {
  for (const recipientId of recipientIds) {
    await pushNotification({
      category: NotificationCategory.PERFORMANCE,
      priority: NotificationPriority.HIGH,
      title: 'Review Cycle Locked',
      message: `${hrAdminName} locked the review cycle "${reviewCycleName}". No further edits allowed.`,
      icon: '🔒',
      link: `/performance/reviews`,
      read: false,
      createdAt: now(),
      recipientId,
      actorName: hrAdminName,
    });
  }
};

/** PMS 12. Cycle Unlocked → notify all managers */
export const notifyReviewCycleUnlocked = async (
  recipientIds: string[],
  hrAdminName: string,
  reviewCycleName: string
) => {
  for (const recipientId of recipientIds) {
    await pushNotification({
      category: NotificationCategory.PERFORMANCE,
      priority: NotificationPriority.MEDIUM,
      title: 'Review Cycle Unlocked',
      message: `${hrAdminName} unlocked the review cycle "${reviewCycleName}". Edits are now allowed.`,
      icon: '🔓',
      link: `/performance/reviews`,
      read: false,
      createdAt: now(),
      recipientId,
      actorName: hrAdminName,
    });
  }
};


// ─── TIMESHEET NOTIFICATIONS ───

/** TMS 1. Employee submits timesheet → notify manager */
export const notifyTimesheetSubmitted = async (
  managerId: string,
  employeeName: string,
  weekLabel: string,
  totalHours: number,
  timesheetId: string,
  employeeId?: string
) => {
  // Notify manager
  if (managerId) {
    await pushNotification({
      category: NotificationCategory.TIMESHEET,
      priority: NotificationPriority.MEDIUM,
      title: 'Timesheet Submitted',
      message: `${employeeName} submitted timesheet for ${weekLabel} (${totalHours}h)`,
      icon: '⏰',
      link: `/timesheet/team`,
      read: false,
      createdAt: now(),
      recipientId: managerId,
      actorName: employeeName,
    });
  }
  // Confirm to employee
  if (employeeId) {
    await pushNotification({
      category: NotificationCategory.TIMESHEET,
      priority: NotificationPriority.LOW,
      title: 'Timesheet Submitted Successfully',
      message: `Your timesheet for ${weekLabel} (${totalHours}h) has been submitted for approval.`,
      icon: '📋',
      link: `/timesheet`,
      read: false,
      createdAt: now(),
      recipientId: employeeId,
    });
  }
};

/** TMS 2. Manager approves timesheet → notify employee + confirm to manager */
export const notifyTimesheetApproved = async (
  employeeId: string,
  managerName: string,
  weekLabel: string,
  managerId?: string
) => {
  // Notify employee
  await pushNotification({
    category: NotificationCategory.TIMESHEET,
    priority: NotificationPriority.MEDIUM,
    title: 'Timesheet Approved',
    message: `${managerName} approved your timesheet for ${weekLabel}`,
    icon: '✅',
    link: `/timesheet`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
  // Confirm to manager
  if (managerId) {
    await pushNotification({
      category: NotificationCategory.TIMESHEET,
      priority: NotificationPriority.LOW,
      title: 'Timesheet Approved',
      message: `You approved the timesheet for ${weekLabel}`,
      icon: '✅',
      link: `/timesheet/team`,
      read: false,
      createdAt: now(),
      recipientId: managerId,
    });
  }
};

/** TMS 3. Manager returns timesheet → notify employee + confirm to manager */
export const notifyTimesheetReturned = async (
  employeeId: string,
  managerName: string,
  weekLabel: string,
  reason: string,
  managerId?: string
) => {
  // Notify employee
  await pushNotification({
    category: NotificationCategory.TIMESHEET,
    priority: NotificationPriority.HIGH,
    title: 'Timesheet Returned',
    message: `${managerName} returned your timesheet for ${weekLabel}: ${reason}`,
    icon: '↩️',
    link: `/timesheet`,
    read: false,
    createdAt: now(),
    recipientId: employeeId,
    actorName: managerName,
  });
  // Confirm to manager
  if (managerId) {
    await pushNotification({
      category: NotificationCategory.TIMESHEET,
      priority: NotificationPriority.LOW,
      title: 'Timesheet Returned',
      message: `You returned the timesheet for ${weekLabel}`,
      icon: '↩️',
      link: `/timesheet/team`,
      read: false,
      createdAt: now(),
      recipientId: managerId,
    });
  }
};

/**
 * In-app notification for a leave decision.
 *
 * The leave module had no in-app notifications at all — unsurprising, since nothing could
 * approve a leave until now. The employee always hears; the approver gets a short
 * confirmation so the action visibly registered.
 */
export const notifyLeaveDecision = async (
  employeeEmail: string,
  employeeName: string,
  approverName: string,
  leaveType: string,
  dateRange: string,
  approved: boolean,
  approverEmail?: string,
  decisionReason?: string
) => {
  await pushNotification({
    category: NotificationCategory.LEAVE,
    priority: approved ? NotificationPriority.MEDIUM : NotificationPriority.HIGH,
    title: approved ? 'Leave Approved' : 'Leave Rejected',
    message: approved
      ? `${approverName} approved your ${leaveType} for ${dateRange}`
      : `${approverName} rejected your ${leaveType} for ${dateRange}${decisionReason ? ` — ${decisionReason}` : ''}`,
    icon: approved ? '✅' : '❌',
    link: '/leave/dashboard',
    read: false,
    createdAt: now(),
    recipientId: employeeEmail,
    actorName: approverName,
  });

  if (approverEmail && approverEmail !== employeeEmail) {
    await pushNotification({
      category: NotificationCategory.LEAVE,
      priority: NotificationPriority.LOW,
      title: approved ? 'Leave approved' : 'Leave rejected',
      message: `You ${approved ? 'approved' : 'rejected'} ${employeeName}'s ${leaveType} for ${dateRange}`,
      icon: approved ? '✅' : '❌',
      link: '/leave/approvals',
      read: false,
      createdAt: now(),
      recipientId: approverEmail,
      actorName: approverName,
    });
  }
};

/**
 * In-app notification when an employee withdraws their own pending request.
 *
 * The manager and HR may already have it in their approvals queue, so they need to know it
 * has gone — otherwise they act on something that no longer exists.
 */
export const notifyLeaveWithdrawn = async (
  employeeEmail: string,
  employeeName: string,
  leaveType: string,
  dateRange: string,
  managerEmail?: string
) => {
  await pushNotification({
    category: NotificationCategory.LEAVE,
    priority: NotificationPriority.LOW,
    title: 'Leave Withdrawn',
    message: `You withdrew your ${leaveType} for ${dateRange}. The days are back in your balance.`,
    icon: '↩️',
    link: '/leave/dashboard',
    read: false,
    createdAt: now(),
    recipientId: employeeEmail,
    actorName: employeeName,
  });

  if (managerEmail && managerEmail !== employeeEmail) {
    await pushNotification({
      category: NotificationCategory.LEAVE,
      priority: NotificationPriority.MEDIUM,
      title: 'Leave Request Withdrawn',
      message: `${employeeName} withdrew their ${leaveType} for ${dateRange}. No action needed.`,
      icon: '↩️',
      link: '/leave/approvals',
      read: false,
      createdAt: now(),
      recipientId: managerEmail,
      actorName: employeeName,
    });
  }
};

/**
 * In-app notification when an employee amends their own request.
 *
 * Distinct from a fresh application because the manager may already have approved the old
 * dates — the amendment sends it back to the queue, and that needs saying out loud.
 */
export const notifyLeaveAmended = async (
  employeeEmail: string,
  employeeName: string,
  leaveType: string,
  dateRange: string,
  wasApproved: boolean,
  managerEmail?: string
) => {
  await pushNotification({
    category: NotificationCategory.LEAVE,
    priority: NotificationPriority.LOW,
    title: 'Leave Updated',
    message: wasApproved
      ? `Your ${leaveType} for ${dateRange} was updated and needs approving again.`
      : `Your ${leaveType} for ${dateRange} was updated.`,
    icon: '✏️',
    link: '/leave/dashboard',
    read: false,
    createdAt: now(),
    recipientId: employeeEmail,
    actorName: employeeName,
  });

  if (managerEmail && managerEmail !== employeeEmail) {
    await pushNotification({
      category: NotificationCategory.LEAVE,
      priority: NotificationPriority.HIGH,
      title: wasApproved ? 'Approved Leave Changed — Re-approval Needed' : 'Leave Request Updated',
      message: `${employeeName} changed their ${leaveType} to ${dateRange}${wasApproved ? '. Your earlier approval no longer applies.' : '.'}`,
      icon: '✏️',
      link: '/leave/approvals',
      read: false,
      createdAt: now(),
      recipientId: managerEmail,
      actorName: employeeName,
    });
  }
};
